import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { Practitioner } from "src/app/ui/models/domains/pbm/practitioner-management/practitioner";
import { SearchPractitionerRequest } from "src/app/ui/models/domains/pbm/practitioner-management/search-practitioner.request";
import { SearchPractitionerResult } from "src/app/ui/models/domains/pbm/practitioner-management/search-practitioner.result";
import { UserPermissions } from "src/app/ui/models/domains/pbm/user/user-permissions";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { PractitionerManagementService } from "src/app/ui/services/pbm/practitioner-management.service";
import { UserService } from "src/app/ui/services/user.service";
import { Toast } from "../../../toast/toast";
import { ConfirmDialog } from "../../shared/dialogs/confirm-dialog/confirm-dialog.component";
import { MatDialogRef, MatDialog } from "@angular/material/dialog";

@Component({
  selector: "search-practitioner",
  templateUrl: "./search-practitioner.component.html",
  styleUrls: ["./search-practitioner.component.scss"],
})
export class SearchPractitionerComponent implements OnInit {
  public filterText: string;
  private result: SearchPractitionerResult;
  private confirmDialogRef: MatDialogRef<ConfirmDialog>;

  constructor(
    private featureViewService: FeatureViewService,
    private practitionerManagementService: PractitionerManagementService,
    private router: Router,
    private loadingService: LoadingService,
    private toast: Toast,
    private userService: UserService,
    private dialog: MatDialog,
  ) {
    this.filterText = "";
    this.result = new SearchPractitionerResult();
  }

  async ngOnInit() {
    this.featureViewService.activeFeatureView(FeatureViews.SearchPractitioner, PageViews.PractitionerManagement);
    const loadingRef = this.loadingService.beginLoading();
    try {
      this.result = await this.practitionerManagementService.searchPractitioners(SearchPractitionerRequest.buildDefaultRequest());
    } finally {
      loadingRef.closeLoading();
    }
  }

  public hasPractitioners(): boolean {
    return this.result.getPractitioners().length > 0;
  }

  public getPractitioners(): Practitioner[] {
    return this.result.getPractitioners();
  }

  public async onFilterSearch() {
    const loadingRef = this.loadingService.beginLoading();
    try {
      const request = new SearchPractitionerRequest();
      request.setFilterText(this.filterText);
      request.setStartIndex(0);
      request.setTotalPerPage(10);
      this.result = await this.practitionerManagementService.searchPractitioners(request);
    } catch (error) {
      this.toast.showWarningToast(error);
    } finally {
      loadingRef.closeLoading();
    }
  }

  public async searchMore() {
    const loadingRef = this.loadingService.beginLoading();
    try {
      const startIndex = this.result.getRequest().getStartIndex() + this.result.getRequest().getTotalPerPage() + 1;
      const request = new SearchPractitionerRequest();
      request.setFilterText(this.filterText);
      request.setStartIndex(startIndex);
      request.setTotalPerPage(10);
      const newResult = await this.practitionerManagementService.searchPractitioners(request);
      this.result.setRequest(request);
      this.result.concatPractitioners(newResult.getPractitioners());
    } catch (error) {
      this.toast.showWarningToast(error.error.Data.ErrorMessage);
    } finally {
      loadingRef.closeLoading();
    }
  }

  public showMoreResultsBtn(): boolean {
    return this.result && this.result.getPractitioners().length > 1 && this.result.getTotal() >= this.result.getPractitioners().length;
  }

  public hasAccessToRegisterPractitioner(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.RegisterPractitioner);
  }

  public hasAccessToEditPractitioner(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditPractitioner);
  }

  public goToRegisterPractitioner(): void {
    this.router.navigate([RoutePath.RegisterPractitioner]);
  }

  public goToEditPractitioner(practitioner: Practitioner): void {
    this.router.navigate([RoutePath.EditPractitioner, practitioner.getKey()]);
  }

  public async deletePractitioner(practitioner: Practitioner): Promise<void> {
    this.confirmDialogRef = this.dialog.open(ConfirmDialog, {
      disableClose: true,
      hasBackdrop: true,
      height: "250px",
      width: "600px",
      data: {
        title: "DELETAR MÉDICO",
        text: `Deseja deletar o médico ${practitioner.getName()}?`,
      },
    });
    this.confirmDialogRef.componentInstance.confirmDialogEvent.subscribe(async () => {
      const loadingRef = this.loadingService.beginLoading();
      try {
        await this.practitionerManagementService.deletePractitioner(practitioner);
        this.result = await this.practitionerManagementService.searchPractitioners(SearchPractitionerRequest.buildDefaultRequest());
      } catch (error) {
        this.toast.showWarningToast("Erro ao deletar médico(a)");
      } finally {
        loadingRef.closeLoading();
      }
    });
  }
}
